import type { GIFObject } from 'giphy-api'
import times from 'lodash/times'

import { IconLoading } from '@/assets/icons/icon-loading'
import SearchIcon from '@/assets/icons/icon-search.svg'
import giphyAttrMarkDark from '@/assets/images/giphy-attr-mark-dark.png'
import giphyAttrMarkLight from '@/assets/images/giphy-attr-mark-light.png'
import { Skeleton } from '@/components/ui/skeleton'
import { TextField } from '@/components/ui/text-field'
import { useIsDarkTheme } from '@/utils/useTheme'

import { useInfiniteGifsData } from './useInfiniteGifsData'

interface Props {
  onClickGif: (giphyId: GIFObject['id']) => void
}

export const GifsList = ({ onClickGif }: Props) => {
  const {
    query,
    list,
    isLoading,
    handleSearchChange,
    handleScroll,
    isFetchingNextPage,
  } = useInfiniteGifsData()
  const isDark = useIsDarkTheme()

  const renderList = () => {
    if (isLoading) {
      return times(5).map((num) => (
        <Skeleton
          key={num}
          className="dark:bg-skeleton h-[120px] w-[120px] shrink-0 rounded-md"
        />
      ))
    }

    return list.map(
      ({ fixed_height_still_url, fixed_height_url, id, title }) => (
        <div
          key={id}
          className="relative h-[120px] w-[120px] shrink-0 cursor-pointer overflow-hidden rounded-md"
          onClick={() => onClickGif(id)}
        >
          <img
            src={fixed_height_still_url}
            alt={title}
            className="h-full w-full object-cover"
          />
          <img
            className="absolute inset-0 h-full w-full object-cover"
            src={fixed_height_url}
            alt={title}
          />
        </div>
      ),
    )
  }

  return (
    <div className="space-y-4">
      <TextField
        placeholder="Search"
        className="dark:border-secondary-400 flex-1 resize-none"
        prefix={<SearchIcon className="text-title" />}
        onChange={handleSearchChange}
        defaultValue={query}
      />
      <div
        onScroll={handleScroll}
        className="no-scrollbar flex gap-2 overflow-y-auto"
      >
        {renderList()}
        {isFetchingNextPage && (
          <div className="bg-card sticky bottom-0 right-0 top-0 flex items-center justify-center p-2">
            <IconLoading className="mx-auto animate-spin" />
          </div>
        )}
      </div>
      <img
        src={isDark ? giphyAttrMarkDark : giphyAttrMarkLight}
        alt="Giphy"
        className="ml-auto w-[180px]"
      />
    </div>
  )
}
